import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelectionList } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment';
import { ValueStore } from 'src/app/add-ons/react-form/redux/list.store';
import { BinderClassification } from 'src/app/models/binder/binder-classification.model';
import { BinderEditForm } from 'src/app/models/binder/binder-edit-form.model';
import { BinderEditJob } from 'src/app/models/binder/binder-edit-job.model';
import { FormResponse } from 'src/app/models/forms/form-response.model';
import { FormTemplate } from 'src/app/models/forms/form-template.model';
import { TokenHelper } from 'src/app/services/helpers/token.helper';
import { BinderWizardService } from 'src/app/services/sections/binder/binder-wizard.service';
import { FormsService } from 'src/app/services/sections/job/forms.service';
import { FormViewerComponent } from '../../form-viewer/form-viewer.component';
import { GenericModalComponent } from '../../generic-modal/generic-modal.component';
import { BinderDetails } from 'src/app/models/binder/binder-details.model';

@Component({
	selector: 'edit-binder',
	templateUrl: './edit-binder.component.html',
	styleUrls: ['./edit-binder.component.scss',
		'../../stylesheets/job-actions.scss',
		'../../stylesheets/job-main.scss',
		'../../stylesheets/job-inputs.scss',
		'../../stylesheets/forms-container.scss',
		'../../generic-modal/generic-modal.component.scss']
})
export class EditBinderComponent implements OnInit, OnChanges {
	@ViewChild('editBinder', { static: true }) public modal: GenericModalComponent;
	@ViewChild('formsModal', { static: true }) public formViewerModal: GenericModalComponent;
	@ViewChild('jobsList') public jobsList: MatSelectionList;
	@ViewChild('infoModal', { static: true }) public infoModal: GenericModalComponent;

	@Input() public years: number[] = null;
	@Input() public selectedYear: number = null;
	@Input() public binderClassification: BinderClassification[] = null;
	@Input() public binderDetails: BinderDetails;

	// tslint:disable-next-line:no-output-on-prefix
	@Output() public onClose: EventEmitter<void> = new EventEmitter<void>();

	public editBinderForm: FormGroup;
	public formLoading: FormGroup;
	public isDataLoading: boolean;
	public isJobLoading = false;
	public isFormSubmitted = false;
	public isJobsEmpty = false;
	public forms: BinderEditForm[] = [];
	public jobs: BinderEditJob[] = [];

	public durationOfRequest: number;
	public currentHour: Date = null;
	public requestCounter = 0;

	public binderParents: BinderClassification[] = null;
	public selectedBinderChildren: BinderClassification[];
	public selectedBinder: BinderClassification;

	public selectedFormTemplate: FormTemplate = {} as FormTemplate;
	public currentFormViewer: FormViewerComponent;

	public tabs = new Map<number, number[]>();
	public formsBeingSaved: { [key: number]: boolean } = {};
	public readonly self = this;
	public moment = moment;
	constructor(
		private binderService: BinderWizardService,
		private activatedRoute: ActivatedRoute,
		private formsService: FormsService
	) { }

	ngOnInit(): void {
		this.initializeTabsForm();
		this.mapBinderContent();

		this.editBinderForm.get('parent').valueChanges.subscribe((parent) => {
			if (parent?.children?.length > 0) {
				const defaultChild = { binderClassificationId: parent.binderClassificationId, binderName: '...' };
				this.selectedBinderChildren = [defaultChild, ...parent.children];
			} else {
				this.selectedBinderChildren = [];
			}
			this.refreshSelection();
		});

		this.editBinderForm.get('year').valueChanges.subscribe(() => {
			this.resetForm();
		});

		this.initializeLoadingForm();

		this.formLoading.get('isFormLoading').valueChanges.subscribe((value) => {
			const refreshRequestTime = (new Date().getMinutes() - this.currentHour.getMinutes());

			if (refreshRequestTime >= 60) {
				this.requestCounter = 0;
				this.currentHour = new Date();
			}
			let delay = 0;
			if (this.durationOfRequest < 2000) {
				delay = 2000 - this.durationOfRequest;
			}
			if (value && this.requestCounter < 3) {
				this.infoModal.open();
			} else {
				setTimeout(() => {
					this.infoModal.hide();
				}, delay);

			}
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!!changes.selectedYear && changes.selectedYear.currentValue !== changes.selectedYear.previousValue && !!this.editBinderForm) {
			this.editBinderForm.patchValue({ year: changes.selectedYear.currentValue });
		}
	}

	public open(): void {
		this.modal.open();
	}

	public close(): void {
		this.modal.hide();
		this.resetForm();
		this.editBinderForm.get('year').setValue(this.selectedYear);
	}

	public resetForm(): void {
		this.editBinderForm.get('parent').patchValue(null);
		this.editBinderForm.get('child').patchValue(null);
		this.editBinderForm.get('form').patchValue(null);
		this.editBinderForm.get('job').patchValue(null);
		this.selectedFormTemplate = {} as FormTemplate;
		this.jobs = [];
		this.selectedBinderChildren = [];
		this.forms = [];
		this.isJobsEmpty = false;
	}

	public initializeTabsForm(): void {
		this.editBinderForm = new FormGroup({
			parent: new FormControl(null),
			child: new FormControl(null),
			year: new FormControl(this.selectedYear),
			form: new FormControl(null),
			job: new FormControl(null)
		});
	}

	public initializeLoadingForm(): void {
		this.formLoading = new FormGroup({
			isFormLoading: new FormControl(false)
		});
	}

	public refreshSelection(): void {
		const parent: BinderClassification =
			this.editBinderForm.get('parent').value;

		if (!parent) {
			return;
		}

		if (parent.children?.length) {
			this.editBinderForm
				.get('child')
				.setValue(this.selectedBinderChildren[0]);
		} else {
			this.editBinderForm.get('child').setValue(null);
		}
	}

	private mapBinderContent(): void {
		const classificationTree = new Map<number, BinderClassification[]>();

		this.binderParents = this.binderClassification.filter(
			(item) => !item.parentBinderClassificationId
		);

		this.binderParents.forEach((item) =>
			classificationTree.set(item.binderClassificationId, [])
		);

		this.binderClassification.forEach((item) => {
			if (
				item.parentBinderClassificationId &&
				classificationTree.has(item.parentBinderClassificationId)
			) {
				classificationTree.get(item.parentBinderClassificationId).push(item);
			}
		});

		this.binderParents = this.binderParents.map((item) => {
			return { ...item, children: classificationTree.get(item.binderClassificationId) };
		});

		if (this.binderParents?.length === 1) {
			this.editBinderForm.get('parent').setValue(this.binderParents[0]);
			this.getForms(this.binderParents[0].binderClassificationId);
		}
	}

	public getForms(binderClassificationId: number): void {
		this.jobs = [];
		const formData = this.editBinderForm.getRawValue();
		this.isDataLoading = true;
		this.isJobsEmpty = false;

		if (formData.parent?.binderClassificationId == null) {
			this.isDataLoading = false;
			return;
		}
		this.binderService.getBinderEditForms(
			TokenHelper.getToken(this.activatedRoute),
			binderClassificationId).subscribe(res => {
				this.forms = res;

				if (res.length === 0) {
					this.editBinderForm.get('form').setValue(null);
				}

				if (this.forms.length === 1) {
					this.editBinderForm.get('form').setValue(this.forms[0]);
					this.getJobs(this.forms[0].formTemplateId);
				}

				this.isDataLoading = false;
			}, error => {
				this.isDataLoading = false;
			});
	}

	public getJobs(formTemplateId: number): void {
		this.jobs = [];
		const formData = this.editBinderForm.getRawValue();
		this.isJobLoading = true;

		if (formData.form?.formTemplateId == null) {
			this.isJobLoading = false;
			return;
		}
		this.binderService.getBinderEditJobs(
			TokenHelper.getToken(this.activatedRoute),
			formTemplateId,
			this.editBinderForm.get('year').value)
			.subscribe(res => {
				if (res.length === 0) {
					this.isJobsEmpty = true;
				}
				this.jobs = res;
				this.isJobLoading = false;
			}, error => {
				this.isJobLoading = false;
			});
	}

	public openFormViewer(): void {
		this.formViewerModal.open();
	}

	public initializeFormTemplate(job: BinderEditJob): void {
		this.editBinderForm.patchValue({ job });

		this.selectedFormTemplate.formTemplateId = job.formTemplateId;
		this.selectedFormTemplate.formName = job.formName;
		this.selectedFormTemplate.formData = job.formData;
		this.selectedFormTemplate.defaultUISchema = job.defaultUiSchema;
		this.selectedFormTemplate.jsonSchema = job.jsonSchema;
		this.selectedFormTemplate.collectedBy = job.submittedBy;
		this.selectedFormTemplate.collectedOn = job.submittedOn;
		this.selectedFormTemplate.jobFormResultId = job.jobFormResultId;
		this.selectedFormTemplate.jobSourcingTierId = job.jobSourcingTierId;
		this.selectedFormTemplate.jobResourceId = job.jobResourceId;
		this.selectedFormTemplate.formDataObj = JSON.parse(job.formData);
		this.selectedFormTemplate.defaultUISchemaObj = JSON.parse(job.defaultUiSchema);
		this.selectedFormTemplate.jsonSchemaObj = JSON.parse(job.jsonSchema);
	}

	public onSubmit(store: ValueStore) {
		if (!this.currentHour) {
			this.currentHour = new Date();
		}
		const startTime = new Date();
		this.isFormSubmitted = true;
		this.formLoading.get('isFormLoading').setValue(true);
		const formData = this.editBinderForm.getRawValue();
		const storeState = store.store.getState();
		const jsonSchemaData = storeState.FormData ? JSON.stringify(storeState.FormData) : null;
		this.formsBeingSaved[formData.job.formTemplateId] = true;

		const response: FormResponse = {
			jobFormResultId: formData.job.jobFormResultId,
			formTemplateId: formData.job.formTemplateId,
			formResult: jsonSchemaData,
			jobId: formData.job.jobId,
			jobSourcingTierId: formData.job.jobSourcingTierId,
			jobResourceId: formData.job.jobResourceId
		};

		this.formsService.updateFormResponseHistory(TokenHelper.getToken(this.activatedRoute), response)
			.subscribe(({ lastUpdatedBy, jobFormResultId, collectedOn }) => {
				const formTemplate = this.selectedFormTemplate;

				if (formTemplate) {
					formTemplate.jobFormResultId = jobFormResultId;
					formTemplate.formData = response.formResult;
					formTemplate.formDataObj = JSON.parse(formTemplate.formData);
					formTemplate.collectedBy = lastUpdatedBy;
					formTemplate.collectedOn = collectedOn;
				}

				this.formsBeingSaved[formTemplate.formTemplateId] = false;

				if (formTemplate.formData !== '{}') {
					const jobItem = this.jobs.find(job => job.jobId === formData.job.jobId && job.formTemplateId === formData.job.formTemplateId);
					jobItem.hasFormData = true;
				}

				this.jobs = this.jobs.map(x => {
					if (x.jobFormResultId === response.jobFormResultId) {
						return {...x, formData: formTemplate.formData};
					} else {
						return x;
					}
				});

				this.jobsList.deselectAll();
				this.durationOfRequest = (new Date().valueOf() - startTime.valueOf());
				this.formLoading.get('isFormLoading').setValue(false);
				this.isFormSubmitted = false;
				this.requestCounter ++;
			}, () => {
				this.jobsList.deselectAll();
				this.resetSelection();
				this.isFormSubmitted = false;
				this.formLoading.get('isFormLoading').setValue(false);
			});

		this.resetSelection();
	}

	public resetSelection(): void {
		if (this.formViewerModal) {
			this.formViewerModal.hide();
		}
		this.editBinderForm.get('job').patchValue(null);
		this.selectedFormTemplate = {} as FormTemplate;
		this.currentFormViewer = null;
	}
}
